import { Head } from "@inertiajs/react";
import CtaBand from "@/components/marketing/cta-band";
import PageHero from "@/components/marketing/page-hero";
import SectionHeading from "@/components/marketing/section-heading";
import SolutionDuo from "@/components/marketing/solution-duo";
import PublicLayout from "@/layouts/public-layout";
import { siteLinks, solutionSummaries } from "@/data/marketing-site";

const highlights = [
  {
    title: "DORA gælder direkte fra 17. januar 2025",
    copy: "Som forordning gælder DORA direkte i Danmark uden national implementeringslov og stiller ens krav i hele EU til den finansielle sektor."
  },
  {
    title: "Reglerne går dybere end klassisk informationssikkerhed",
    copy: "Det er ikke nok at have politikker. DORA kræver, at organisationen kan bevise modstandsdygtighed, reaktionsevne og evne til at holde driften kørende under angreb."
  },
  {
    title: "Forsyningskæden er en central del af kravet",
    copy: "Finansielle kunder sender DORA-kravene videre til IKT-tredjeparter og andre kritiske leverandører, også når de ikke selv er direkte omfattet."
  }
];

const requirementAreas = [
  {
    icon: "bi bi-shield-check",
    title: "IKT-risikostyring",
    copy: "Organisationen skal have en dokumenteret og ledelsesforankret model til at identificere, styre og reducere digitale risici."
  },
  {
    icon: "bi bi-broadcast",
    title: "Hændelseshåndtering og rapportering",
    copy: "IKT-relaterede hændelser skal klassificeres, håndteres og rapporteres i overensstemmelse med de gældende DORA-frister og myndighedskrav."
  },
  {
    icon: "bi bi-activity",
    title: "Test af digital modstandsdygtighed",
    copy: "Finansielle enheder skal demonstrere, at de kan modstå, reagere på og komme sig efter angreb gennem løbende test og validering."
  },
  {
    icon: "bi bi-buildings",
    title: "Tredjepartsrisikostyring",
    copy: "Kontrakter, kritikalitet, afhængigheder og løbende tilsyn med IKT-tredjeparter skal være samlet og dokumenterbare."
  },
  {
    icon: "bi bi-people",
    title: "Informationsdeling",
    copy: "DORA åbner for struktureret informationsdeling mellem enheder som en del af at styrke den samlede digitale modstandsdygtighed."
  }
];

const coverageChecks = [
  {
    title: "Banker, forsikring og finansielle markedsaktører",
    copy: "DORA gælder bredt for banker, realkreditinstitutter, forsikringsselskaber, investeringsselskaber, betalingsaktører og andre finansielle enheder."
  },
  {
    title: "Størrelsen ændrer sjældent grundkravet",
    copy: "De fleste finansielle aktører er omfattet uanset størrelse, med kun få og smalle undtagelser."
  },
  {
    title: "Kritiske IKT-tredjeparter mærker også presset",
    copy: "Selv uden at være direkte omfattet vil mange leverandører opleve, at finansielle kunder kræver DORA-tilpasset styring, kontrakter og dokumentation."
  }
];

const consequenceRows = [
  {
    title: "Personligt ledelsesansvar"
  },
  {
    title: "Bøder og påbud"
  },
  {
    title: "Inspektioner og dyb dokumentationskontrol"
  },
  {
    title: "Påvirkning af kunder og samarbejdspartnere"
  }
];

function Dora() {
  return (
    <>
      <Head title="DORA">
        <meta
          head-key="description"
          name="description"
          content="DORA stiller krav til digital operationel modstandsdygtighed i finanssektoren. Se hvordan Obsero hjælper jer fra krav til dokumenterbar drift."
        />
      </Head>

      <PageHero
        narrow
        size="compact"
        title="DORA kræver bevist modstandsdygtighed, ikke bare gode intentioner."
        copy="DORA er EU's forordning for digital operationel modstandsdygtighed i finanssektoren og gælder direkte fra 17. januar 2025. Fokus er ikke kun sikkerhed på papir, men evnen til at modstå, reagere på og komme sig efter hændelser i praksis."
        actions={[
          { href: siteLinks.demo, label: "Book et møde", className: "btn btn-light" }
        ]}
      />

      <section className="public-section public-section--white-surface">
        <div className="public-container">
          <div className="public-split-callout">
            <div className="public-split-callout__lead public-split-callout__lead--accented">
              <SectionHeading
                title="Et dybere og mere direkte regelsæt for finanssektoren."
                copy="Hvor NIS2 dækker bredt, er DORA målrettet den finansielle sektor med dybere krav, direkte tilsyn og skarpere forventninger til test, hændelsesrapportering og tredjepartsstyring."
              />
            </div>

            <div className="public-stack-list">
              {highlights.map((item, index) => (
                <article key={item.title} className="public-stack-item">
                  <div className="public-stack-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-stack-item__body">
                    <h3 className="public-stack-item__title">{item.title}</h3>
                    <p className="public-stack-item__copy">{item.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>

        </div>
      </section>

      <section className="public-section public-section--shell-band">
        <div className="public-container">
          <SectionHeading
            eyebrow="Hvem er omfattet af DORA?"
            title="DORA gælder bredt i finanssektoren og mærkes også af leverandører."
          />

          <div className="public-pillars public-pillars--spacious">
            {coverageChecks.map((item, index) => {
              const icons = ["bi bi-bank", "bi bi-arrows-angle-expand", "bi bi-link-45deg"];

              return (
                <article key={item.title} className="public-pillar">
                  <div className="public-icon-chip">
                    <i className={icons[index]} aria-hidden="true" />
                  </div>
                  <div className="public-pillar__body">
                    <h3 className="public-pillar__title">{item.title}</h3>
                    <p className="public-pillar__copy">{item.copy}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="public-section public-section--white-band" id="krav">
        <div className="public-container">
          <SectionHeading
            title="DORA stiller krav inden for fem områder."
          />

          <div className="public-module-rail">
            {requirementAreas.map((item, index) => (
              <article key={item.title} className="public-module-row">
                <div className="public-module-row__index">{String(index + 1).padStart(2, "0")}</div>
                <div className="public-module-row__body">
                  <h3 className="public-module-row__title">{item.title}</h3>
                  <p className="public-module-row__copy">{item.copy}</p>
                </div>
                <div className="public-icon-chip" aria-hidden="true">
                  <i className={item.icon} />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="public-section public-section--solution-band" id="loesninger">
        <div className="public-container">
          <SectionHeading
            title="Sådan kan Obsero hjælpe dig med DORA."
          />

          <SolutionDuo
            items={solutionSummaries.map((solution) => ({
              key: solution.key,
              title: solution.label,
              copy: solution.copy,
              fit: solution.key === "core"
                ? ["DORA-compliant på kun 12 uger.", ...solution.fit]
                : solution.fit,
              href: solution.href
            }))}
          />
        </div>
      </section>

      <section className="public-section public-section--white-band">
        <div className="public-container">
          <div className="public-split-callout">
            <div className="public-split-callout__lead">
              <SectionHeading
                title="DORA er allerede under aktivt tilsyn."
                copy="Finanstilsynet er allerede i gang med de første inspektioner. Når regulator forventer dokumenteret modstandsdygtighed, bliver svage processer, manglende test og uklart ledelsesansvar hurtigt et forretningsproblem."
              />
            </div>

            <div className="public-stack-list">
              {consequenceRows.map((item, index) => (
                <article key={item.title} className="public-stack-item">
                  <div className="public-stack-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-stack-item__body">
                    <h3 className="public-stack-item__title">{item.title}</h3>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        eyebrow="DORA i praksis"
        title="Er I klar til at tage det næste skridt?"
        copy="Vi gennemgår gerne løsningen, viser de centrale artefakter og gør det tydeligt, hvordan I kommer fra lovkrav til dokumenterbar compliance i drift."
        actions={[
          { href: siteLinks.contact, label: "Kontakt os", className: "btn btn-light" }
        ]}
      />
    </>
  );
}

Dora.layout = (page) => <PublicLayout>{page}</PublicLayout>;

export default Dora;
